import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, User, FileText, Edit, CheckCircle, XCircle, Send, Eye } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

interface ActivityLogEntry {
  id: string;
  action_type: string;
  resource_type: string;
  resource_title: string | null;
  details: any;
  created_at: string;
  user?: {
    id: string;
    name: string;
    email: string;
  } | null;
}

interface PolicyActivityLogProps {
  policyId: string;
  policyTitle: string;
}

export function PolicyActivityLog({ policyId, policyTitle }: PolicyActivityLogProps) {
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const { profile } = useAuth();

  useEffect(() => {
    if (policyId && profile?.company_id) {
      fetchPolicyActivities();
    }
  }, [policyId, profile?.company_id]);

  const fetchPolicyActivities = async () => {
    if (!profile?.company_id || !policyId) return;
    
    try {
      const { data: activitiesData, error } = await supabase
        .from('activity_logs')
        .select(`
          id,
          action_type,
          resource_type,
          resource_title,
          details,
          created_at,
          user:profiles(id, name, email)
        `)
        .eq('company_id', profile.company_id)
        .eq('resource_id', policyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setActivities(activitiesData || []);
    } catch (error) {
      console.error('Error fetching policy activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType: string) => {
    const iconMap: Record<string, any> = {
      'policy_created': FileText,
      'policy_edited': Edit,
      'policy_sent_to_draft': FileText,
      'policy_reviewed': Eye,
      'policy_approved': CheckCircle,
      'policy_rejected': XCircle,
      'policy_deleted': XCircle,
      'workflow_status_changed': Send,
    };
    return iconMap[actionType] || Clock;
  };

  const getActionColor = (actionType: string) => {
    const colorMap: Record<string, string> = {
      'policy_created': 'bg-green-100 text-green-800',
      'policy_edited': 'bg-blue-100 text-blue-800',
      'policy_sent_to_draft': 'bg-yellow-100 text-yellow-800',
      'policy_reviewed': 'bg-purple-100 text-purple-800',
      'policy_approved': 'bg-green-100 text-green-800',
      'policy_rejected': 'bg-red-100 text-red-800',
      'policy_deleted': 'bg-red-100 text-red-800',
      'workflow_status_changed': 'bg-orange-100 text-orange-800',
    };
    return colorMap[actionType] || 'bg-gray-100 text-gray-800';
  };

  const formatActionType = (actionType: string) => {
    const actionMap: Record<string, string> = {
      'policy_created': 'Policy Created',
      'policy_edited': 'Policy Edited',
      'policy_sent_to_draft': 'Sent to Draft',
      'policy_reviewed': 'Policy Reviewed',
      'policy_approved': 'Policy Approved',
      'policy_rejected': 'Policy Rejected',
      'policy_deleted': 'Policy Deleted',
      'workflow_status_changed': 'Status Changed',
    };
    return actionMap[actionType] || actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getActivityDescription = (activity: ActivityLogEntry) => {
    const details = activity.details;
    
    switch (activity.action_type) {
      case 'policy_created':
        return 'Policy was created and saved as draft';
      case 'policy_edited':
        return 'Policy content or details were modified';
      case 'workflow_status_changed':
        if (details?.from_status && details?.to_status) {
          return `Status changed from "${details.from_status}" to "${details.to_status}"`;
        }
        return 'Policy status was updated';
      case 'policy_approved':
        return 'Policy was approved and is now active';
      case 'policy_rejected':
        return 'Policy was rejected and needs revision';
      default:
        return details?.action || 'Activity performed on policy';
    }
  };

  const displayedActivities = showAll ? activities : activities.slice(0, 5);

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Audit Trail
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Audit Trail
          </CardTitle>
          <Link to="/activity-log">
            <Button variant="outline" size="sm">
              View All Activities
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="mx-auto h-8 w-8 mb-2 opacity-50" />
            <p>No activities recorded for this policy yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {displayedActivities.map((activity) => {
              const ActionIcon = getActionIcon(activity.action_type);
              
              return (
                <div key={activity.id} className="flex gap-3 p-3 rounded-lg bg-muted/30">
                  <div className="flex-shrink-0">
                    <div className={`p-2 rounded-full ${getActionColor(activity.action_type)}`}>
                      <ActionIcon className="w-4 h-4" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getActionColor(activity.action_type)}>
                            {formatActionType(activity.action_type)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(activity.created_at), "MMM d, yyyy 'at' h:mm a")}
                          </span>
                        </div>
                        
                        <p className="text-sm text-foreground mb-1">
                          {getActivityDescription(activity)}
                        </p>
                        
                        {activity.user && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <User className="w-3 h-3" />
                            <span>by {activity.user.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Additional details */}
                    {activity.details && typeof activity.details === 'object' && activity.details.changes && (
                      <div className="mt-2 p-2 bg-muted rounded text-xs">
                        <span className="font-medium">Changes made:</span>
                        <pre className="mt-1 whitespace-pre-wrap">{JSON.stringify(activity.details.changes, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {activities.length > 5 && (
              <div className="text-center pt-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? 'Show Less' : `Show All ${activities.length} Activities`}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
